import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, ArrowRight, ArrowLeft, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, playWordTwice, spellOut, preloadLetters, unlockAudioPlayback, interruptCurrentAudio } from "../lib/api";
import { shuffle } from "../lib/shuffle";
import WordImage from "../components/WordImage";
import ImageToggle from "../components/ImageToggle";
import useShowImage from "../hooks/useShowImage";
import useSwipe from "../hooks/useSwipe";
import { Shuffle } from "lucide-react";

export default function Practice() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [order, setOrder] = useState([]);
  const [idx, setIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [speakingWord, setSpeakingWord] = useState(false);
  const [speakingLetters, setSpeakingLetters] = useState(false);
  const [def, setDef] = useState(null);
  const [showImage, setShowImage] = useShowImage(true); // default on in practice
  const [spellingIdx, setSpellingIdx] = useState(null); // which letter is speaking (null = none)

  // Warm up the 26-letter cache on mount so "Hear letters" is always instant
  // and every letter is pronounced reliably.
  useEffect(() => {
    // Practice auto-plays the first word on load. The navigation click
    // that brought us here is a valid user gesture, so kick the iOS
    // audio unlock just in case Layout's listener missed it.
    unlockAudioPlayback();
    preloadLetters();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/wordlists/${id}`);
        setList(data);
        setOrder(shuffle(data.words || []));
        setIdx(0);
      } catch { toast.error("List not found"); navigate("/wordlists"); }
    })();
  }, [id, navigate]);

  const word = order[idx];

  useEffect(() => {
    if (!word) return;
    // `cancelled` is closed-over by every async step below so a rapid word
    // change (StrictMode double-mount, kid tapping Next, or shuffle re-run)
    // can short-circuit any in-flight work for the OLD word. Without this,
    // the old playWordTwice's secondary-voice playback would fire ~500ms
    // after the new word started — cutting the new word in half.
    let cancelled = false;
    setRevealed(false);
    setDef(null);
    setSpellingIdx(null);
    // auto-pronounce twice when the word changes
    (async () => {
      if (cancelled) return;
      try {
        setSpeakingWord(true);
        await playWordTwice(word, 450, { shouldStop: () => cancelled });
      } catch { /* ignore */ }
      finally { if (!cancelled) setSpeakingWord(false); }
      if (cancelled) return;
      try {
        const { data } = await api.post("/words/definition", { word });
        if (!cancelled) setDef(data);
      } catch {
        if (!cancelled) setDef({ definition: "A word to practice.", example: `I can spell ${word}.` });
      }
    })();
    return () => {
      cancelled = true;
      // Stop the main audio element immediately so the user doesn't hear
      // the trailing half of the old word.
      interruptCurrentAudio();
    };
  }, [word]);

  const pronounceWord = async () => {
    if (!word) return;
    setSpeakingWord(true);
    try {
      await playWordTwice(word);
    } catch (err) {
      console.warn("[pronounceWord] failed:", err);
      toast.error("Couldn't play audio");
    } finally { setSpeakingWord(false); }
  };

  const pronounceLetters = async () => {
    if (!word) return;
    setSpeakingLetters(true);
    try {
      await spellOut(word, {
        onLetter: (i) => setSpellingIdx(i >= 0 ? i : null),
      });
    }
    catch (err) {
      console.warn("[pronounceLetters] failed:", err);
      toast.error("Couldn't play audio");
    }
    finally { setSpeakingLetters(false); setSpellingIdx(null); }
  };

  const next = () => { if (order.length && idx < order.length - 1) setIdx(idx + 1); };
  const prev = () => { if (idx > 0) setIdx(idx - 1); };
  const swipe = useSwipe({ onSwipeLeft: next, onSwipeRight: prev });
  const reshuffle = () => { setOrder(shuffle(list.words)); setIdx(0); toast.success("Shuffled!"); };

  if (!list || !order.length) return <div className="card-soft text-center py-12 text-slate-500">Loading…</div>;

  const progress = order.length ? ((idx + 1) / order.length) * 100 : 0;

  return (
    <div className="space-y-6" data-testid="practice-page" {...swipe}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-sm font-bold text-[#4D96FF] uppercase tracking-widest">Practice mode</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#1E1B4B]">{list.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={reshuffle} data-testid="practice-shuffle-btn" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full font-bold text-sm bg-white text-[#1E1B4B] border-2 border-blue-200 hover:bg-blue-50 transition-colors" title="Shuffle words">
            <Shuffle className="w-4 h-4" strokeWidth={3} /> Shuffle
          </button>
          <ImageToggle show={showImage} onToggle={setShowImage} testId="practice-image-toggle" />
          <div className="chip" data-testid="progress-indicator">
            Word {idx + 1} of {order.length}
          </div>
        </div>
      </div>

      <div className="h-3 w-full rounded-full bg-blue-100 overflow-hidden">
        <motion.div
          className="h-full bg-[#10B981]"
          animate={{ width: `${progress}%` }}
          transition={{ type: "spring", bounce: 0.2 }}
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={word}
          initial={{ y: 30, opacity: 0, scale: 0.9 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: -30, opacity: 0, scale: 0.95 }}
          transition={{ type: "spring", bounce: 0.4 }}
          className="card-soft text-center py-10"
          data-testid="practice-card"
        >
          <Sparkles className="w-8 h-8 mx-auto text-[#FBBF24]" strokeWidth={3} />
          <div className="text-sm font-bold uppercase text-slate-500 mt-2">Tap the speaker to hear the word</div>

          <div className="mt-5">
            <WordImage word={word} visible={showImage} size="lg" thumb />
          </div>

          <div className="my-6">
            {(() => {
              const letters = (word || "").toUpperCase().split("");
              const showingLetters = revealed || spellingIdx !== null;
              return (
                <div className="flex items-end justify-center gap-1 sm:gap-2 flex-wrap" data-testid="word-letters">
                  {letters.map((L, i) => {
                    const isCurrent = spellingIdx === i;
                    const isPast = spellingIdx !== null && i < spellingIdx;
                    const visible = showingLetters && (spellingIdx === null || i <= spellingIdx);
                    const color = isCurrent
                      ? "text-[#FBBF24]"
                      : isPast
                        ? "text-[#10B981]"
                        : visible
                          ? "text-[#FF6B6B]"
                          : "text-slate-300";
                    return (
                      <motion.span
                        key={`${word}-${i}`}
                        data-testid={`letter-${i}`}
                        initial={false}
                        animate={{
                          scale: isCurrent ? 1.25 : 1,
                          y: isCurrent ? -6 : 0,
                        }}
                        transition={{ type: "spring", stiffness: 500, damping: 18 }}
                        className={`font-display text-6xl sm:text-7xl font-bold ${color} tracking-wider inline-block`}
                      >
                        {visible ? L : "?"}
                      </motion.span>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            <button onClick={pronounceWord} disabled={speakingWord} className="btn-clay-blue" data-testid="hear-word-btn">
              <Volume2 className={`w-5 h-5 ${speakingWord ? "pulse-soft" : ""}`} strokeWidth={3} />
              {speakingWord ? "Speaking…" : "Hear word"}
            </button>
            <button onClick={pronounceLetters} disabled={speakingLetters} className="btn-clay-yellow" data-testid="spell-letters-btn">
              <Volume2 className={`w-5 h-5 ${speakingLetters ? "pulse-soft" : ""}`} strokeWidth={3} />
              {speakingLetters ? "Spelling…" : "Hear letters"}
            </button>
            {!revealed ? (
              <button onClick={() => setRevealed(true)} className="btn-clay-green" data-testid="show-word-btn">
                <CheckCircle2 className="w-5 h-5" strokeWidth={3} /> Show me!
              </button>
            ) : (
              <button onClick={() => setRevealed(false)} className="btn-clay-ghost" data-testid="hide-word-btn">
                Hide word
              </button>
            )}
          </div>

          {def && (
            <div className="max-w-xl mx-auto mt-8 text-left bg-blue-50 rounded-2xl p-5" data-testid="definition-panel">
              <div className="text-xs font-bold uppercase text-[#4D96FF]">Meaning</div>
              <div className="text-base font-semibold text-[#1E1B4B]">{def.definition}</div>
              <div className="text-xs font-bold uppercase text-[#4D96FF] mt-3">Example</div>
              <div className="text-base italic text-slate-700">"{def.example}"</div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Bottom spacer so the sticky nav never covers content */}
      <div className="h-24 sm:h-4" aria-hidden="true" />

      <div
        className="sticky bottom-0 -mx-5 px-5 py-3 bg-[#F4F9FF]/90 backdrop-blur-md border-t-2 border-blue-100 z-30 flex items-center justify-between gap-3"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 0.75rem)" }}
        data-testid="practice-nav-bar"
      >
        <button
          onClick={prev}
          disabled={idx === 0}
          className="btn-clay-ghost disabled:opacity-40 flex-1 sm:flex-initial"
          data-testid="prev-btn"
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={3} /> Previous
        </button>
        {idx === order.length - 1 ? (
          <button
            onClick={() => navigate(`/test/${list.id}`)}
            className="btn-clay flex-1 sm:flex-initial"
            data-testid="go-test-btn"
          >
            Ready to test <ArrowRight className="w-5 h-5" strokeWidth={3} />
          </button>
        ) : (
          <button
            onClick={next}
            className="btn-clay-blue flex-1 sm:flex-initial"
            data-testid="next-btn"
          >
            Next <ArrowRight className="w-5 h-5" strokeWidth={3} />
          </button>
        )}
      </div>
    </div>
  );
}
